import { useEffect, useRef, useState } from 'react';

export type AnnouncerMessage = {
  id: string;
  role?: string;
  text: string;
};

export type ChatLiveAnnouncerProps = {
  /** Messages in render order; the announcer watches for assistant messages that finish streaming. */
  messages: AnnouncerMessage[];
  /** id of the message currently streaming, if any */
  streamingMessageId?: string | null;
  isStreaming: boolean;
};

export type Announcement = {
  id: string;
  text: string;
};

/** Pure helper: which assistant messages are ready to announce once. */
export function selectAnnouncements(
  messages: AnnouncerMessage[],
  streamingMessageId: string | null | undefined,
  alreadyAnnounced: Set<string>,
): Announcement[] {
  const selected: Announcement[] = [];
  for (const message of messages) {
    if (message.role === 'user') continue;
    const text = message.text?.trim();
    if (!text) continue;
    if (alreadyAnnounced.has(message.id)) continue;
    if (message.id === streamingMessageId) continue;
    selected.push({ id: message.id, text });
  }
  return selected;
}

export function ChatLiveAnnouncer({
  messages,
  streamingMessageId,
  isStreaming: _isStreaming,
}: ChatLiveAnnouncerProps) {
  const announcedRef = useRef(new Set<string>());
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);

  useEffect(() => {
    const next = selectAnnouncements(messages, streamingMessageId, announcedRef.current);
    if (next.length === 0) return;

    for (const item of next) {
      announcedRef.current.add(item.id);
    }

    setAnnouncements((prev) => [...prev, ...next].slice(-3));
  }, [messages, streamingMessageId]);

  return (
    <div className="sr-only" role="log" aria-live="polite" aria-relevant="additions">
      {announcements.map((announcement) => (
        <p key={announcement.id}>{announcement.text}</p>
      ))}
    </div>
  );
}
